'use client';

import { useState } from 'react';
import { Target, Plus, Check, Edit2, Trash2, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  usePlayerGoals,
  useCreateGoal,
  useUpdateGoal,
  useDeleteGoal,
  type Goal,
  type CreateGoalData,
  type UpdateGoalData,
} from '@/hooks/use-goals';

interface PlayerGoalsProps {
  playerId: string;
}

const GOAL_CATEGORIES = [
  { value: 'technical', label: 'Technical', color: 'bg-blue-100 text-blue-700' },
  { value: 'tactical', label: 'Tactical', color: 'bg-purple-100 text-purple-700' },
  { value: 'physical', label: 'Physical', color: 'bg-green-100 text-green-700' },
  { value: 'mental', label: 'Mental', color: 'bg-amber-100 text-amber-700' },
] as const;

const GOAL_TYPES = [
  { value: 'short_term', label: 'Short-term' },
  { value: 'long_term', label: 'Long-term' },
] as const;

export function PlayerGoals({ playerId }: PlayerGoalsProps) {
  const { data, isLoading } = usePlayerGoals(playerId);
  const createGoal = useCreateGoal(playerId);
  const updateGoal = useUpdateGoal();
  const deleteGoal = useDeleteGoal();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [completingGoal, setCompletingGoal] = useState<Goal | null>(null);

  const activeGoals = data?.goals.filter(g => g.status === 'active') ?? [];
  const completedGoals = data?.goals.filter(g => g.status === 'completed') ?? [];

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-20 bg-muted animate-pulse rounded-lg" />
        <div className="h-20 bg-muted animate-pulse rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-muted-foreground" />
          <span className="font-medium">Goals</span>
          <Badge variant="outline">{activeGoals.length} active</Badge>
        </div>
        <Button size="sm" onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-1" />
          Add Goal
        </Button>
      </div>

      {/* Active Goals */}
      {activeGoals.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No active goals set yet.</p>
          <p className="text-sm">Click &ldquo;Add Goal&rdquo; to create the first goal.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activeGoals.map(goal => (
            <GoalCard
              key={goal.id}
              goal={goal}
              onEdit={() => setEditingGoal(goal)}
              onComplete={() => setCompletingGoal(goal)}
              onDelete={() => {
                if (confirm('Are you sure you want to delete this goal?')) {
                  deleteGoal.mutateAsync(goal.id);
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Completed Goals */}
      {completedGoals.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-muted-foreground mb-3">
            Completed ({completedGoals.length})
          </h4>
          <div className="space-y-2">
            {completedGoals.slice(0, 5).map(goal => (
              <CompletedGoalCard
                key={goal.id}
                goal={goal}
                onUncomplete={() => {
                  updateGoal.mutateAsync({
                    goalId: goal.id,
                    data: { status: 'active' },
                  });
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Create Goal Modal */}
      <GoalFormModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onSubmit={async (data) => {
          await createGoal.mutateAsync(data as CreateGoalData);
          setShowCreateModal(false);
        }}
        isSubmitting={createGoal.isPending}
      />

      {/* Edit Goal Modal */}
      <GoalFormModal
        open={!!editingGoal}
        onOpenChange={(open) => !open && setEditingGoal(null)}
        goal={editingGoal ?? undefined}
        onSubmit={async (data) => {
          if (editingGoal) {
            await updateGoal.mutateAsync({ goalId: editingGoal.id, data: data as UpdateGoalData });
            setEditingGoal(null);
          }
        }}
        isSubmitting={updateGoal.isPending}
      />

      {/* Complete Goal Modal */}
      <CompleteGoalModal
        goal={completingGoal}
        open={!!completingGoal}
        onOpenChange={(open) => !open && setCompletingGoal(null)}
        onSubmit={async (notes) => {
          if (completingGoal) {
            await updateGoal.mutateAsync({
              goalId: completingGoal.id,
              data: { status: 'completed', completionNotes: notes || null },
            });
            setCompletingGoal(null);
          }
        }}
        isSubmitting={updateGoal.isPending}
      />
    </div>
  );
}

function GoalCard({
  goal,
  onEdit,
  onComplete,
  onDelete,
}: {
  goal: Goal;
  onEdit: () => void;
  onComplete: () => void;
  onDelete: () => void;
}) {
  const category = GOAL_CATEGORIES.find(c => c.value === goal.category);

  return (
    <div className="p-4 border border-border rounded-lg bg-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Badge className={cn('text-xs', category?.color)}>
              {category?.label}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {goal.type === 'short_term' ? 'Short-term' : 'Long-term'}
            </Badge>
            {goal.targetDate && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(goal.targetDate).toLocaleDateString()}
              </span>
            )}
          </div>
          <h4 className="font-medium text-foreground">{goal.title}</h4>
          {goal.description && (
            <p className="text-sm text-muted-foreground mt-1">{goal.description}</p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-green-600 hover:bg-green-50"
            onClick={onComplete}
            title="Mark as complete"
          >
            <Check className="w-4 h-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={onEdit}
            title="Edit goal"
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-red-600 hover:bg-red-50"
            onClick={onDelete}
            title="Delete goal"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function CompletedGoalCard({ goal, onUncomplete }: { goal: Goal; onUncomplete: () => void }) {
  const category = GOAL_CATEGORIES.find(c => c.value === goal.category);

  return (
    <div className="p-3 border border-border rounded-lg bg-muted/50 opacity-75">
      <div className="flex items-start gap-2">
        <button
          onClick={onUncomplete}
          className="mt-0.5 flex-shrink-0 hover:opacity-70 transition"
          title="Mark as active"
        >
          <Check className="w-4 h-4 text-green-500" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge className={cn('text-xs', category?.color)} variant="outline">
              {category?.label}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {goal.completedAt && new Date(goal.completedAt).toLocaleDateString()}
            </span>
          </div>
          <p className="text-sm text-muted-foreground line-through">{goal.title}</p>
          {goal.completionNotes && (
            <p className="text-xs text-muted-foreground mt-1 italic">
              &ldquo;{goal.completionNotes}&rdquo;
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function GoalFormModal({
  open,
  onOpenChange,
  goal,
  onSubmit,
  isSubmitting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goal?: Goal;
  onSubmit: (data: CreateGoalData | UpdateGoalData) => void;
  isSubmitting: boolean;
}) {
  const [type, setType] = useState<'short_term' | 'long_term'>(goal?.type ?? 'short_term');
  const [category, setCategory] = useState<string>(goal?.category ?? 'technical');
  const [title, setTitle] = useState(goal?.title ?? '');
  const [description, setDescription] = useState(goal?.description ?? '');
  const [targetDate, setTargetDate] = useState(goal?.targetDate ?? '');

  const isEditing = !!goal;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    const data: CreateGoalData | UpdateGoalData = {
      type,
      category: category as CreateGoalData['category'],
      title: title.trim(),
      description: description.trim() || undefined,
      targetDate: targetDate || undefined,
    };

    onSubmit(data);
  };

  // Reset form when modal opens/closes or goal changes
  const resetForm = () => {
    setType(goal?.type ?? 'short_term');
    setCategory(goal?.category ?? 'technical');
    setTitle(goal?.title ?? '');
    setDescription(goal?.description ?? '');
    setTargetDate(goal?.targetDate ?? '');
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) resetForm();
        onOpenChange(open);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Goal' : 'Add New Goal'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-foreground">Type</label>
              <Select value={type} onValueChange={(v) => setType(v as typeof type)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GOAL_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GOAL_CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground">Goal Title</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Improve first serve percentage to 60%"
              className="mt-1"
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground">Description (optional)</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add more details about this goal..."
              className="mt-1"
              rows={3}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground">Target Date (optional)</label>
            <Input
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className="mt-1"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !title.trim()}>
              {isSubmitting ? 'Saving...' : isEditing ? 'Update Goal' : 'Add Goal'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CompleteGoalModal({
  goal,
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
}: {
  goal: Goal | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (notes: string) => void;
  isSubmitting: boolean;
}) {
  const [notes, setNotes] = useState('');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Complete Goal</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {goal && (
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="font-medium">{goal.title}</p>
              {goal.description && (
                <p className="text-sm text-muted-foreground mt-1">{goal.description}</p>
              )}
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-foreground">
              Completion Notes (optional)
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about how the goal was achieved..."
              className="mt-1"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit(notes)}
            disabled={isSubmitting}
            className="bg-primary hover:bg-primary/90"
          >
            {isSubmitting ? 'Saving...' : 'Mark Complete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
